import { LoadingContent, useAnimatedList } from '../components/feedback';
import { api } from '../api/client';
import type { ChangeRequest } from '../api/types';
import { RequestReview } from '../components/RequestReview';
import { Badge, Empty, ErrorAlert } from '../components/ui';
import { formatRelative, requestKindLabel, requestStatusInfo, viaLabel } from '../format';
import { usePoll } from '../hooks/usePoll';
import { RequestHistory } from '../components/RequestHistory';

function RequestRow({ request, onChange }: { request: ChangeRequest; onChange: () => void }) {
  const status = requestStatusInfo[request.status] ?? { label: request.status, tone: 'muted' as const };
  return (
    <div className="card request-card">
      <div className="card-header">
        <span className="row" style={{ gap: 10 }}>
          {request.autoExecuted && <Badge tone="muted">AI が実行</Badge>}
          <Badge tone={status.tone}>{status.label}</Badge>
          <strong>{requestKindLabel[request.kind] ?? request.kind}</strong>
          {request.serverId && <span className="muted">{request.serverId}</span>}
        </span>
        <span className="small faint">
          {request.requestedBy}（{viaLabel[request.via] ?? request.via}）・{formatRelative(request.createdAt)}
        </span>
      </div>
      <div className="card-body">
        <RequestReview request={request} onDecided={onChange} compact automatic={false} />
      </div>
    </div>
  );
}

export function Requests() {
  const requests = usePoll(() => api.requests({ status: 'all' }), 5000);
  const all = requests.data ?? [];
  const pending = all.filter((r) => r.status === 'pending');
  const pendingList = useAnimatedList(pending, (request) => request.id);
  const history = all.filter((r) => r.status !== 'pending');
  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1 data-search-id="requests">承認</h1>
          <p>削除・復元などの影響が大きい操作は、ここで内容を確認してから実行します。AI からの依頼もここに届きます。</p>
        </div>
      </div>
      <ErrorAlert error={requests.error} />
      <section className="stack" style={{ gap: 10 }}>
        <h3>承認待ち（{pending.length}）</h3>
        <LoadingContent loading={!requests.data && requests.loading}>
          {pendingList.items.length === 0 ? (
            <div className="card">
              <Empty>承認待ちの依頼はありません。</Empty>
            </div>
          ) : (
            pendingList.items.map((r) => (
              <div
                key={r.id}
                ref={pendingList.ref}
                className={pendingList.className(r)}
                inert={!pending.some((item) => item.id === r.id)}
              >
                <RequestRow request={r} onChange={() => void requests.reload()} />
              </div>
            ))
          )}
        </LoadingContent>
      </section>
      {history.length > 0 && (
        <section className="stack" style={{ gap: 10 }}>
          <h3>履歴</h3>
          <RequestHistory requests={history} />
        </section>
      )}
    </div>
  );
}
